// Permission checking utilities for granular access control

export type PermissionKey = 
  | 'warranty'
  | 'call_center'
  | 'contact_center'
  | 'monitoring'
  | 'errors'
  | 'analytics'
  | 'performance'
  | 'plans'
  | 'customers'
  | 'reviews'
  | 'imports'
  | 'integrations'
  | 'sales'
  | 'memberships'
  | 'traffic'
  | 'conversions'
  | 'api'
  | 'renewals'
  | 'audit'
  | 'users'
  | 'exports'
  | 'settings';

export interface UserPermissions {
  role: 'admin' | 'editor' | 'manager' | 'user' | 'readonly';
  permissions?: Record<string, boolean>;
}

/**
 * Check if user has permission to access a feature
 * @param user User object with role and permissions
 * @param permission Permission key to check
 * @returns true if user has access, false otherwise
 */
export function hasPermission(
  user: UserPermissions | null | undefined,
  permission: PermissionKey
): boolean {
  if (!user) return false;
  
  // ONLY admin role has full access - NO OTHER ROLES
  if (user.role === 'admin') {
    return true;
  }
  
  // Read-only users can view all pages but cannot edit
  if (user.role === 'readonly') {
    return true; // Allow viewing, but not editing
  }
  
  // Regular users - check permissions object for specific page access
  if (user.role === 'user') {
    return user.permissions?.[permission] === true;
  }
  
  return false;
}

/**
 * Check if user has ANY of the listed permissions
 */
export function hasAnyPermission(
  user: UserPermissions | null | undefined,
  permissions: PermissionKey[]
): boolean {
  return permissions.some(p => hasPermission(user, p));
}

/**
 * Check if user has ALL of the listed permissions
 */
export function hasAllPermissions(
  user: UserPermissions | null | undefined,
  permissions: PermissionKey[]
): boolean {
  return permissions.every(p => hasPermission(user, p));
}

/**
 * Check if user is admin
 * ONLY the 'admin' role is considered admin - no other roles have full access
 */
export function isAdmin(user: UserPermissions | null | undefined): boolean {
  return user?.role === 'admin';
}
